import { useEffect, useRef, useState } from "react";
import { SheetDialog } from "./SheetDialog";
import { NumPadKeys } from "./NumPadKeys";
import { grp, NB, useNumPadInput } from "../../hooks/useNumPadInput";
import { blurOtherField } from "./padFocus";
import css from "./NumPad.module.css";

/**
 * NumPad — React-порт design-references/numpad.js по семантике. Кастомная
 * клавиатура для денег/чисел, системная клавиатура ОС не поднимается.
 * Управляемый компонент, роль-акцент — из html[data-role] (var(--role)).
 *
 * Модель коммита НЕЯВНАЯ (в отличие от TimePicker): ЛЮБОЕ закрытие —
 * backdrop, Esc, клавиша ✓, программное open=false — это commit
 * (onCommit(val) ровно один раз, защита committedRef). Кнопок
 * «Готово/Отмена» нет; «изменилось ли» диффает хост (old !== v).
 *
 * Логика ввода (fresh, long-press ⌫ 480мс, max → shake + hint 1400мс) —
 * общий хук useNumPadInput (делится с NumPadPop). onNext: футер «Далее →»
 * — commit текущего + применение opts следующей ячейки БЕЗ закрытия шита,
 * превью мигает (npIn).
 */
export interface NumPadOpts {
  label?: string;
  mode?: "money" | "int";
  value?: number | string;
  /** Юнит для int-режима (например «чел»). */
  unit?: string;
  presets?: (number | string)[];
  max?: number;
  onCommit?: (v: number) => void;
  /** Вернуть opts следующей ячейки — применятся без закрытия; null — закрыть. */
  onNext?: (v: number) => NumPadOpts | null | undefined;
}

export interface NumPadProps extends NumPadOpts {
  open: boolean;
  /** Хост закрывает шит (open=false); вызывается ПОСЛЕ commit. */
  onClose: () => void;
  /** Поверх всего, включая TimePicker и экранные модалки. */
  zIndex?: number | string;
}

/* Мердж как в эталонном applyOpts (NP:113): только определённые ключи. */
function mergeOpts(base: NumPadOpts, o: NumPadOpts): NumPadOpts {
  const out: Record<string, unknown> = { ...base };
  for (const [k, v] of Object.entries(o)) if (v !== undefined) out[k] = v;
  return out as NumPadOpts;
}

export function NumPad(props: NumPadProps) {
  const { open, onClose, zIndex = "calc(var(--z-modal) + 60)" } = props;
  const [cur, setCur] = useState<NumPadOpts>({});
  const [fx, setFx] = useState<{ kind: "" | "shk" | "nx"; n: number }>({ kind: "", n: 0 });
  const committedRef = useRef(true);
  const optsRef = useRef<NumPadOpts>({});
  optsRef.current = {
    label: props.label,
    mode: props.mode,
    value: props.value,
    unit: props.unit,
    presets: props.presets,
    max: props.max,
    onCommit: props.onCommit,
    onNext: props.onNext,
  };

  const mode = cur.mode === "money" ? "money" : "int";
  const input = useNumPadInput({
    mode,
    unit: cur.unit,
    max: cur.max,
    onBump: () => setFx((f) => ({ kind: "shk", n: f.n + 1 })),
  });
  const inputRef = useRef(input);
  inputRef.current = input;

  const initFrom = (o: NumPadOpts) => {
    inputRef.current.init(o.value);
    committedRef.current = false;
  };

  /* Опции фиксируются в момент открытия (дальше ими правит только onNext). */
  useEffect(() => {
    if (!open) return;
    blurOtherField(); /* 06.09 (владелец): одна клавиатура за раз — фокус (и клавиатура ОС) уходит из чужого поля */
    const o = optsRef.current;
    setCur(o);
    initFrom(o);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const commit = () => {
    if (committedRef.current) return;
    committedRef.current = true;
    cur.onCommit?.(input.val());
  };

  /* Любое закрытие = commit, включая программное open=false хостом. */
  useEffect(() => {
    if (!open) commit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const dismiss = () => {
    commit();
    onClose();
  };

  const goNext = () => {
    const v = input.val();
    commit();
    const nxt = cur.onNext?.(v);
    if (nxt) {
      /* onCommit НЕ наследуется (эталон NP:70 обнуляет st.onCommit после
         коммита; applyOpts возвращает его только из next-opts) — иначе при
         закрытии колбэк предыдущей ячейки выстрелит со значением новой. */
      const merged = mergeOpts({ ...cur, onCommit: undefined }, nxt);
      setCur(merged);
      initFrom(merged);
      setFx((f) => ({ kind: "nx", n: f.n + 1 }));
    } else onClose();
  };

  const chips = (cur.presets ?? []).map((p) =>
    typeof p === "number" ? p : parseInt(String(p).replace(/\D/g, ""), 10),
  );

  return (
    <SheetDialog open={open} onDismiss={dismiss} zIndex={zIndex} maxHeight="60dvh" ariaLabel={cur.label ?? "Число"}>
      <div className={css.head}>
        <span className={css.label}>{cur.label ?? "Число"}</span>
        <span
          key={fx.n}
          className={`${css.preview} ${fx.kind === "shk" ? css.shk : ""} ${fx.kind === "nx" ? css.nx : ""}`}
        >
          {input.digits === "" ? (
            <span className={`${css.val} ${css.dash}`}>—</span>
          ) : mode === "money" ? (
            <span className={css.val}>₽{grp(input.digits)}</span>
          ) : (
            <>
              <span className={css.val}>{input.digits}</span>
              {cur.unit ? <span className={css.unit}>{cur.unit}</span> : null}
            </>
          )}
        </span>
      </div>
      <div className={`${css.hint} ${input.hint.on ? css.on : ""}`}>{input.hint.text}</div>
      {chips.length > 0 ? (
        <div className={css.presets}>
          {chips.map((n, i) => (
            <button key={`${i}-${n}`} type="button" className={css.chip} onClick={() => input.set(n)}>
              {mode === "money" ? `₽${grp(String(n))}` : `${n}${cur.unit ? NB + cur.unit : ""}`}
            </button>
          ))}
        </div>
      ) : null}
      <div className={css.zone}>
        <NumPadKeys
          onDigit={input.append}
          onOk={dismiss}
          onDelDown={input.delDown}
          onDelUp={input.delUp}
          onDelClick={input.delClick}
        />
        <div className={css.scan} />
      </div>
      {cur.onNext ? (
        <div className={css.foot}>
          <button type="button" className={css.next} onClick={goNext}>
            Далее →
          </button>
        </div>
      ) : null}
    </SheetDialog>
  );
}
